import { observer } from 'mobx-react';
import { Document } from '@roc/feature-types';
import { useBorrowerTrackRecordStore } from '@roc/feature-borrowers';
import { useUserStore } from '@roc/feature-app-core';
import { TrackRecordV2 } from '@roc/feature-track-record-v2';
import { CustomTaskDialog, TaskStatus, useGeneralTasksStore } from '@roc/feature-general-tasks';
import { useEffect } from 'react';
import { Dialog, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(() => ({
  crmPaper: {
    '& [class*="makeStyles-container"]': {
      height: 'unset',
    },
  },
}));

interface TrackRecordLineItemDialogProps {
  open: boolean;
  onClose: () => void;
  loanId: any;
  document: Document;
  taskId?: number;
}

export const TrackRecordLineItemDialog = observer(({ open, onClose, loanId, document, taskId }: TrackRecordLineItemDialogProps) => {
  const classes = useStyles();
  const { borrowerTrackRecordStore } = useBorrowerTrackRecordStore();
  const { userStore } = useUserStore();
  const { generalTasksStore } = useGeneralTasksStore();

  // Borrower this Track Record belongs to. Comes straight off the document
  const resolvedBorrowerId = document?.borrowerId ?? null;
  // With a real task, keep the full task shell (Notes/Assign/Complete); bare modal only when there's none.
  const resolvedTaskId = document?.taskId ?? taskId;
  // Same calls as the task's "Mark as complete": isStatusChangeReady, then markTaskAsCompleted.
  const handleAccept = resolvedTaskId
    ? () => generalTasksStore.updateTaskStatusV2(resolvedTaskId, TaskStatus.ACCEPTED, onClose)
    : undefined;

  useEffect(() => {
    if (!borrowerTrackRecordStore) return;
    borrowerTrackRecordStore.setDocument(document);
    // Internal (roc-internal-portal) uses the internal download API; crm-portal
    // uses the public one. Drives BaseBorrowerTrackRecordStore.downloadDocument().
    borrowerTrackRecordStore.internal = userStore.isNportalUser;
  }, [document, borrowerTrackRecordStore, userStore.isNportalUser]);

  // Surface a clear error if the document arrived without a borrowerId
  useEffect(() => {
    if (open && document && document.borrowerId == null) {
      console.error(
        '[TrackRecord] Missing borrowerId on the Track Record document — cannot reliably scope licenses to a borrower.',
        { taskId: resolvedTaskId, loanId },
      );
    }
  }, [open, document, resolvedTaskId, loanId]);

  // NOTE: keep this early return below all hook calls (Rules of Hooks).
  if (!borrowerTrackRecordStore) {
    return <></>;
  }

  // Full-width grid item for CustomTask's <Grid container>; hideTabs drops V2's own Chat tab (CustomTask has one).
  const trackRecordContent = (
    <Grid item xs={12} style={{ width: '100%' }}>
      <TrackRecordV2
        onClose={onClose}
        borrowerName={document?.borrowerName}
        store={borrowerTrackRecordStore}
        borrowerId={resolvedBorrowerId}
        taskId={resolvedTaskId}
        isInternal={userStore.isNportalUser}
        onAccept={handleAccept}
        hideTaskTitle
        hideTabs
        // Fit inside the CustomTaskDialog content (title + tabs + margins ≈ 246px) so only V2 scrolls.
        maxHeight="calc(100vh - 250px)"
      />
    </Grid>
  );

  if (resolvedTaskId) {
    return (
      <CustomTaskDialog
        open={open}
        maxWidth="xl"
        handleClose={onClose}
        title={'Track Record'}
        PaperProps={{
          className: classes.crmPaper,
          style: { padding: '0px' },
        }}
        dialogContent={trackRecordContent}
        taskId={resolvedTaskId}
      />
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ style: { background: 'transparent', boxShadow: 'none', overflow: 'visible' } }}
    >
      <TrackRecordV2
        onClose={onClose}
        borrowerName={document?.borrowerName}
        store={borrowerTrackRecordStore}
        borrowerId={resolvedBorrowerId}
        taskId={resolvedTaskId}
        isInternal={userStore.isNportalUser}
        onAccept={handleAccept}
      />
    </Dialog>
  );
});
